import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Sparkles, ArrowLeft, Award, Star, Clock,
  MessageCircle, Mic, Camera, Lightbulb, BookOpen,
  Rocket, Target, GraduationCap, Globe, Compass,
  Search, HelpCircle, BrainCircuit, ImageIcon, Zap, BookMarked,
  Flame, CheckCircle2, LogOut, ClipboardList, ChevronRight, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import TrialBanner from '@/components/TrialBanner';
import LockedAccount from '@/components/LockedAccount';

const UNIFIED_MODES = [
  { id: 'learn', label: 'Doubts', subtitle: "Ask anything, let's figure it out together", icon: Lightbulb, color: 'from-teal-400 to-cyan-500' },
  { id: 'quiz', label: 'Practice', subtitle: "Pick a topic, I'll quiz you on it", icon: BrainCircuit, color: 'from-rose-400 to-red-500' },
  { id: 'summarize', label: 'Notes', subtitle: "Upload your notes, I'll help you understand it", icon: BookMarked, color: 'from-blue-400 to-indigo-500' },
];

const AGE_GREETINGS = {
  '5-7': (name) => `Hi ${name}! I found something really cool today...`,
  '7-9': (name) => `Hey ${name}! Ready to discover something amazing?`,
  '9-11': (name) => `Hey ${name}! I've got something interesting for you...`,
  '11-13': (name) => `${name}, I've been thinking about something you might find interesting...`,
  '13-15': (name) => `${name}, here's something that connects ideas in a fascinating way...`,
};

const CONTENT_ICONS = {
  story: BookOpen,
  expedition: Rocket,
  mission: Target,
  deep_dive: GraduationCap,
};

const CONTENT_COLORS = {
  story: { gradient: 'from-amber-400 to-orange-500', bg: 'from-amber-50 to-orange-50', accent: 'text-amber-600', btn: 'bg-amber-500 hover:bg-amber-600', border: 'border-amber-100' },
  expedition: { gradient: 'from-blue-400 to-indigo-500', bg: 'from-blue-50 to-indigo-50', accent: 'text-blue-600', btn: 'bg-blue-500 hover:bg-blue-600', border: 'border-blue-100' },
  mission: { gradient: 'from-teal-400 to-cyan-500', bg: 'from-teal-50 to-cyan-50', accent: 'text-teal-600', btn: 'bg-teal-500 hover:bg-teal-600', border: 'border-teal-100' },
  deep_dive: { gradient: 'from-rose-400 to-pink-500', bg: 'from-rose-50 to-pink-50', accent: 'text-rose-600', btn: 'bg-rose-500 hover:bg-rose-600', border: 'border-rose-100' },
};

export default function ChildHomeComplete() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [dailySpark, setDailySpark] = useState(null);
  const [dailyExpedition, setDailyExpedition] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStudentAccount, setIsStudentAccount] = useState(false);
  const [trialInfo, setTrialInfo] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    fetchChildData();
    api.get('/auth/me').then(res => {
      setUserInfo(res.data);
      if (res.data?.is_student_account) setIsStudentAccount(true);
      if (res.data?.is_locked) setIsLocked(true);
    }).catch(() => {});
    // Fetch trial status
    api.get('/auth/trial-status').then(res => {
      setTrialInfo(res.data);
      if (res.data?.is_locked) setIsLocked(true);
    }).catch(() => {});
  }, [childId]);

  const handleLogout = () => {
    localStorage.removeItem('yukti_session_token');
    document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    // School students return to the branded school login; B2C → marketing landing.
    if (child?.source === 'school' && school?.slug) {
      window.location.href = `/school/${school.slug}/login`;
    } else {
      window.location.href = '/';
    }
  };

  const fetchChildData = async () => {
    try {
      const [childRes, badgesRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/child/${childId}/badges`),
      ]);
      setChild(childRes.data);
      setBadges(badgesRes.data);

      // Fetch daily spark (non-blocking) — triggers generation if needed
      api.get(`/chat/daily-spark/${childId}`).then(res => {
        if (res?.data) setDailySpark(res.data);
      }).catch(() => null);

      // Fetch daily expedition (legacy, non-blocking)
      api.get(`/child/${childId}/daily-expedition`).then(res => {
        if (res?.data) setDailyExpedition(res.data);
      }).catch(() => null);
    } catch (error) {
      console.error('Failed to fetch child data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const [showFamilyCode, setShowFamilyCode] = useState(false);
  const school = useSchool(child?.school_id);
  const [assessmentSummary, setAssessmentSummary] = useState(null);

  // Pending assessments summary — school students only
  useEffect(() => {
    if (child?.source !== 'school') {
      setAssessmentSummary(null);
      return;
    }
    api.get('/me/assessments')
      .then((res) => {
        const items = res.data || [];
        const pending = items.filter(
          (a) => a.student_status === 'not_started' || a.student_status === 'in_progress'
        );
        const awaiting = items.filter((a) => a.student_status === 'submitted').length;
        // Soonest pending due date
        let soonest = null;
        for (const a of pending) {
          if (!a.due_at) continue;
          const d = new Date(a.due_at);
          if (!soonest || d < soonest.date) {
            soonest = { date: d, title: a.title, isOverdue: !!a.is_overdue };
          }
        }
        setAssessmentSummary({
          total: items.length,
          pendingCount: pending.length,
          awaitingCount: awaiting,
          soonest,
        });
      })
      .catch(() => setAssessmentSummary({ total: 0, pendingCount: 0, awaitingCount: 0, soonest: null }));
  }, [child?.source, child?.child_id]);

  const openChatWithMode = (mode) => {
    navigate(`/child/${childId}/chat/new?mode=${mode}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-400 mx-auto mb-3" />
          <p className="text-orange-600 font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  if (isLocked) {
    return <LockedAccount parent={userInfo} onLogout={handleLogout} />;
  }

  const ageBand = child?.age_band || '7-9';
  const modes = UNIFIED_MODES;
  const greeting = (AGE_GREETINGS[ageBand] || AGE_GREETINGS['7-9'])(child?.name || 'Explorer');
  const contentType = dailySpark?.content_type?.type || 'expedition';
  const contentPlural = dailySpark?.content_type?.plural || 'Expeditions';
  const ContentIcon = CONTENT_ICONS[contentType] || Rocket;
  const colors = CONTENT_COLORS[contentType] || CONTENT_COLORS.expedition;

  return (
    <div className="min-h-screen bg-gray-50">
      {school && (
        <div
          className="bg-white border-b shadow-sm"
          style={{ borderTopWidth: 4, borderTopColor: school.brand_color || BRAND_COLOR_FALLBACK }}
          data-testid="student-school-stripe"
        >
          <div className="max-w-3xl mx-auto px-4 py-2 flex items-center gap-2">
            {school.logo_url ? (
              <img
                src={school.logo_url}
                alt={school.name}
                className="w-7 h-7 rounded object-contain bg-white border"
                data-testid="student-school-logo"
              />
            ) : (
              <div
                className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: school.brand_color || BRAND_COLOR_FALLBACK }}
              >
                {school.name?.charAt(0) || 'S'}
              </div>
            )}
            <span
              className="text-sm font-semibold text-slate-700"
              data-testid="student-school-name-welcome"
            >
              {school.name}
            </span>
          </div>
        </div>
      )}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {isStudentAccount ? (
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          ) : (
            <Button
              onClick={() => navigate('/family')}
              data-testid="back-to-family"
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          <div className="flex items-center gap-3">
            {child?.family_code && child?.source !== 'school' && (
              <div className="relative">
                <button
                  data-testid="show-family-code-btn"
                  onClick={() => setShowFamilyCode(!showFamilyCode)}
                  className="text-xs text-purple-600 bg-purple-50 px-3 py-1.5 rounded-full font-medium hover:bg-purple-100 transition-colors"
                >
                  Family Code
                </button>
                {showFamilyCode && (
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-purple-100 p-4 z-50 w-56">
                    <p className="text-xs text-gray-500 mb-1">Share this code with your parent</p>
                    <p className="text-xl font-mono font-bold text-purple-700 tracking-widest" data-testid="family-code-display">{child.family_code}</p>
                    <button
                      onClick={() => { navigator.clipboard.writeText(child.family_code); setShowFamilyCode(false); }}
                      className="text-xs text-purple-600 mt-2 hover:underline"
                    >
                      Copy to clipboard
                    </button>
                  </div>
                )}
              </div>
            )}
            {child?.streak > 0 && (
              <div className="flex items-center gap-1 text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                <Flame className="w-4 h-4" />
                <span className="text-sm font-bold">{child.streak}</span>
              </div>
            )}
            <div className="text-sm font-bold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
              {child?.total_xp || 0} XP
            </div>
          </div>
        </div>

        {/* Trial Period Banner — B2C only, hidden for school-enrolled children */}
        {trialInfo && !trialInfo.is_expired && child?.source !== 'school' && (
          <div className="mb-4">
            <TrialBanner trialEnd={trialInfo.trial_end} />
          </div>
        )}

        {/* Yukti Greeting Card — THE primary element */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="rounded-2xl bg-white shadow-lg border border-gray-100 overflow-hidden mb-6"
        >
          {/* Yukti Header */}
          <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-orange-50 to-pink-50 border-b border-orange-100">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 flex items-center justify-center shadow-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800" data-testid="yukti-greeting-name">Yukti</h2>
              <p className="text-xs text-gray-400">Your learning companion</p>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-green-600 font-medium">Online</span>
            </div>
          </div>

          {/* Greeting Message */}
          <div className="p-5">
            <p className="text-gray-800 leading-relaxed" data-testid="yukti-greeting-text">{greeting}</p>
          </div>

          {/* Daily Curiosity Spark */}
          {dailySpark?.spark && (
            <div className={`mx-5 mb-4 rounded-xl p-4 bg-gradient-to-br ${colors.bg} border ${colors.border}`}>
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className={`w-4 h-4 ${colors.accent}`} />
                <span className={`text-xs font-bold ${colors.accent} uppercase tracking-wide`}>Today's Curiosity Spark</span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed mb-2" data-testid="spark-question">
                {dailySpark.spark.question}
              </p>
              <p className={`${colors.accent} font-semibold text-sm mb-4`} data-testid="spark-followup">
                {dailySpark.spark.followUp}
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => {
                    const sparkQ = dailySpark?.spark?.question || '';
                    const sparkTopic = dailySpark?.spark?.topic || '';
                    const sparkParam = encodeURIComponent(sparkQ || sparkTopic);
                    navigate(`/child/${childId}/chat/new?mode=learn${sparkParam ? `&spark=${sparkParam}` : ''}`);
                  }}
                  data-testid="spark-chat-btn"
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r ${colors.gradient} text-white font-bold text-sm shadow-md hover:shadow-lg transition-all`}
                >
                  <MessageCircle className="w-4 h-4" /> Chat about this
                </button>
              </div>
            </div>
          )}

          {/* Quick reply chips */}
          <div className="px-5 pb-4 flex flex-wrap gap-2">
            {['Tell me more!', 'Something else', 'Surprise me'].map((chip, i) => (
              <button
                key={i}
                onClick={() => openChatWithMode('learn')}
                className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full hover:bg-gray-200 transition-all"
              >
                {chip}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Assessments Promo Card — school students only (Epic 1B promotion) */}
        {child?.source === 'school' && assessmentSummary !== null && (
          assessmentSummary.pendingCount > 0 ? (
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              onClick={() => navigate('/student/assessments')}
              data-testid="assessments-promo-card"
              className="w-full text-left mb-6 rounded-2xl bg-white shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-all"
            >
              <div
                className="px-5 py-2.5 flex items-center gap-2 text-white text-xs font-bold uppercase tracking-wider"
                style={{ backgroundColor: school?.brand_color || BRAND_COLOR_FALLBACK }}
              >
                <ClipboardList className="w-3.5 h-3.5" />
                <span>Assessments</span>
                {assessmentSummary.awaitingCount > 0 && (
                  <span
                    className="ml-auto bg-white/25 px-2 py-0.5 rounded-full text-[10px]"
                    data-testid="assessments-awaiting-pill"
                  >
                    {assessmentSummary.awaitingCount} awaiting teacher
                  </span>
                )}
              </div>
              <div className="px-5 py-4 flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
                  style={{ backgroundColor: school?.brand_color || BRAND_COLOR_FALLBACK }}
                >
                  <span className="text-2xl" data-testid="assessments-pending-count">
                    {assessmentSummary.pendingCount}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 text-base leading-tight">
                    {assessmentSummary.pendingCount === 1
                      ? '1 assessment pending'
                      : `${assessmentSummary.pendingCount} assessments pending`}
                  </div>
                  {assessmentSummary.soonest && (
                    <div
                      className={`flex items-center gap-1 mt-1 text-xs ${assessmentSummary.soonest.isOverdue ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}
                      data-testid="assessments-soonest-due"
                    >
                      {assessmentSummary.soonest.isOverdue
                        ? <AlertCircle className="w-3.5 h-3.5" />
                        : <Clock className="w-3.5 h-3.5" />}
                      <span>
                        {assessmentSummary.soonest.isOverdue ? 'Overdue · ' : 'Due '}
                        {assessmentSummary.soonest.date.toLocaleDateString(undefined, {
                          weekday: 'short', day: 'numeric', month: 'short',
                        })}
                        {' · '}{assessmentSummary.soonest.title}
                      </span>
                    </div>
                  )}
                </div>
                <span
                  className="text-sm font-semibold flex items-center gap-1 flex-shrink-0"
                  style={{ color: school?.brand_color || BRAND_COLOR_FALLBACK }}
                  data-testid="assessments-promo-cta"
                >
                  Open <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </motion.button>
          ) : (
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              onClick={() => navigate('/student/assessments')}
              data-testid="assessments-caught-up-line"
              className="w-full mb-6 flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors px-1"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              <span>All caught up on assessments
                {assessmentSummary.awaitingCount > 0 && (
                  <span className="text-amber-600 font-medium">
                    {' '}· {assessmentSummary.awaitingCount} awaiting teacher
                  </span>
                )}
              </span>
              <span className="ml-auto underline">View past</span>
            </motion.button>
          )
        )}

        {/* Learning Modes */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mb-6"
        >
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3 px-1">How can Yukti help you today?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {modes.map((mode) => (
              <motion.button
                key={mode.id}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => openChatWithMode(mode.id)}
                data-testid={`mode-${mode.id}`}
                className="flex items-center gap-3 p-4 sm:p-5 bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all text-left"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${mode.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  <mode.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <span className="font-bold text-gray-800 text-sm">{mode.label}</span>
                  <p className="text-xs text-gray-500 leading-snug mt-0.5">{mode.subtitle}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Bottom Row: Stats */}
        <div>
          {/* Stats */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl p-4 bg-white shadow-md border border-gray-100"
          >
            <h3 className="font-bold text-gray-700 text-sm mb-3">Your Progress</h3>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center">
                <div className="text-xl font-bold text-orange-500">{child?.streak || 0}</div>
                <div className="text-[10px] text-gray-400">Day Streak</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-blue-500">{child?.total_xp || 0}</div>
                <div className="text-[10px] text-gray-400">Total XP</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-purple-500">{badges.length}</div>
                <div className="text-[10px] text-gray-400">Badges</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Chat History Link */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35 }}
          onClick={() => navigate(`/child/${childId}/chat`)}
          data-testid="chat-history-link"
          className="mt-4 rounded-xl p-3 bg-white shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-all flex items-center gap-3"
        >
          <MessageCircle className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-600 font-medium">View past conversations</span>
          <span className="ml-auto text-xs text-gray-400">{'>'}</span>
        </motion.div>
      </div>
    </div>
  );
}
